import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { PageNav, SocialMediaProfilesMenu } from '@components/common';
import { DefaultAvatar } from '@constants/defaults';
import { SocialMediaLogos } from '@constants/socialMedia';
import { useAppSelector } from '@hooks/index';
import PageWrapper from '@pages/base/PageWrapper';
import { SocialMediaSingleProfileItem } from '@pages/social-media/details/SocialMediaDetails';
import useFetchActiveProfiles from '@pages/social-media/details/useFetchActiveProfiles';
import useFetchNetworkData from '@pages/social-media/details/useFetchNetworkData';
import { DefinedRoutes, OnlySocialMediaRoutes } from '@routes/definedRoutes';
import { getSocialMediaSingleUrl } from '@routes/definedRoutesUtils';
import { capitalizeFirstLetter } from '@services/conversion/conversion';
import { isArrayEmpty } from '@services/validation/validation';
import { DataStatus } from '@shared/enum/dataStatus';
import { SocialMediaTypes } from '@shared/enum/socialMediaTypes';
import { SocialMediaSingleActiveProfiles } from '@shared/interfaces/social-media';
import { selectedFromUser } from '@store/authentication/authentication.reducer';
import { selectFromSocialMediaSingle } from '@store/social-media/single/SocialMediaSingle.slice';

const NetworkTypeToLogoMapper = {
    [SocialMediaTypes.FACEBOOK]: SocialMediaLogos.facebook.filled,
    [SocialMediaTypes.INSTAGRAM]: SocialMediaLogos.instagram.filled,
    [SocialMediaTypes.TWITTER]: SocialMediaLogos.twitter.filled,
    [SocialMediaTypes.YOUTUBE]: SocialMediaLogos.youtube.filled,
    [SocialMediaTypes.TIKTOK]: SocialMediaLogos.tiktok.filled,
};

export interface SinglesLayoutContextType {
    profiles:  SocialMediaSingleProfileItem[];
    currentProfile: SocialMediaSingleProfileItem;
    currentProfileId: string;
    currentNetworkType: SocialMediaTypes;

    activeProfiles: SocialMediaSingleActiveProfiles;
    activeProfilesStatus: DataStatus;
    hasActiveProfiles: boolean;

    networkData: ReturnType<typeof selectFromSocialMediaSingle.all>;
}

const SocialMediaSinglesLayout = () => {
    const navigate            = useNavigate();
    const { pathname, state } = useLocation();
    const { profileId }       = useParams();
    const currentNetworkType  = (state?.networkType as SocialMediaTypes) || SocialMediaTypes.UNKNOWN;
    const { onlySocialMedia } = useAppSelector(selectedFromUser.settings);

    const { activeProfiles, status: activeProfilesStatus } = useFetchActiveProfiles(profileId);
    const hasActiveProfiles                                = (
        activeProfilesStatus === DataStatus.SUCCESS &&
        !isArrayEmpty(activeProfiles) &&
        !!activeProfiles.find(profile => profile.status === true)
    );
    const profiles: SocialMediaSingleProfileItem[]         = isArrayEmpty(activeProfiles)
        ? []
        : activeProfiles.map((profile, idx) => profile.status === true ? ({
            id: String(idx+1),
            isSelected: profile.profileId === profileId,
            networkThumbnail: profile?.network ? NetworkTypeToLogoMapper?.[profile.network] : null,
            networkType: profile.network as SocialMediaTypes,
            profile: profile.name,
            profileId: profile.profileId,
            thumbnail: profile?.thumbnail || DefaultAvatar,
            url: profile.url,
        }) : null).filter(Boolean);
    const currentProfile: SocialMediaSingleProfileItem     = profiles.find(profile => profile.isSelected);

    const { data } = useFetchNetworkData(profileId, hasActiveProfiles);

    const networkType = (() =>{
        if (currentNetworkType) {
            return currentNetworkType;
        }

        if (onlySocialMedia) {
            if (OnlySocialMediaRoutes.facebook.validator(pathname)) {
                return SocialMediaTypes.FACEBOOK;
            }

            if (OnlySocialMediaRoutes.instagram.validator(pathname)) {
                return SocialMediaTypes.INSTAGRAM;
            }

            if (OnlySocialMediaRoutes.tiktok.validator(pathname)) {
                return SocialMediaTypes.TIKTOK;
            }

            if (OnlySocialMediaRoutes.twitter.validator(pathname)) {
                return SocialMediaTypes.TWITTER;
            }

            return;
        }

        if (DefinedRoutes.socialMedia.singles.facebook.validator(pathname)) {
            return SocialMediaTypes.FACEBOOK;
        }

        if (DefinedRoutes.socialMedia.singles.instagram.validator(pathname)) {
            return SocialMediaTypes.INSTAGRAM;
        }

        if (DefinedRoutes.socialMedia.singles.tiktok.validator(pathname)) {
            return SocialMediaTypes.TIKTOK;
        }

        if (DefinedRoutes.socialMedia.singles.twitter.validator(pathname)) {
            return SocialMediaTypes.TWITTER;
        }

        return SocialMediaTypes.YOUTUBE;
    })();

    const isInAllPosts   = useMemo(() => {
        if (onlySocialMedia) {
            return OnlySocialMediaRoutes?.[networkType]?.posts?.validator?.(pathname) ?? false;
        }

        return DefinedRoutes.socialMedia.singles?.[networkType]?.posts?.validator?.(pathname) ?? false;
    }, [networkType, pathname]);
    const isInAllStories = useMemo(() => {
        if (onlySocialMedia) {
            return OnlySocialMediaRoutes?.[networkType]?.stories?.validator?.(pathname) ?? false;
        }

        return DefinedRoutes.socialMedia.singles?.[networkType]?.stories?.validator?.(pathname) ?? false;
    }, [networkType, pathname]);

    const postTitles = {
        [SocialMediaTypes.TWITTER]: 'All tweets',
        [SocialMediaTypes.INSTAGRAM]: 'All posts',
        [SocialMediaTypes.FACEBOOK]: 'All posts',
        [SocialMediaTypes.YOUTUBE]: 'All posts',
        [SocialMediaTypes.TIKTOK]: 'All videos',
    };

    return (
        <PageWrapper
            wrapperId="dashboard_wrapper"
            pageTitle={`Social media | ${currentNetworkType}`}
            headerInfo={isInAllPosts || isInAllStories ? (
                <PageNav
                    parent='Social media'
                    childrenPages={[
                        {
                            name: capitalizeFirstLetter(String(currentNetworkType)),
                            path: getSocialMediaSingleUrl(currentNetworkType, profileId) || ''
                        },
                        {
                            name: isInAllStories ? 'All stories' : postTitles[networkType],
                            path: ''
                        },
                    ]}
                />
            ) : (
                <SocialMediaProfilesMenu
                    isLoading={activeProfilesStatus === DataStatus.LOADING}
                    onChangeSelectedProfile={(profile) => {
                        const pathToDetails = getSocialMediaSingleUrl(profile.networkType, profile.profileId);

                        navigate(pathToDetails);
                    }}
                    profiles={profiles}
                />
            )}
            // disableDownloadReport
        >
            <div>
                <Outlet
                    context={{
                        profiles,
                        currentProfile,
                        currentProfileId: profileId,
                        currentNetworkType,

                        activeProfiles,
                        activeProfilesStatus,
                        hasActiveProfiles,

                        networkData: data,
                    } as SinglesLayoutContextType}
                />
            </div>
        </PageWrapper>
    );
};

export default SocialMediaSinglesLayout;
